import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { NewExposureMap } from "../../hooks/feeLab/usePathDragChanges";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatNumberFloor, formatPercentage } from "../../utils/namesFormater";
import { formatProductType } from "../../utils/enumFormatter";
import { SimulationChangeDetectionUtil } from "../../services/userTask/simulationChangeDetectionUtil";
interface Props {
  newExposureMap: NewExposureMap;
  modifiedRows: MimshakData[];
  originalRows: MimshakData[];
  onRemovePolicy: (policyNumber: string) => void;
  onOpenPathDrawer?: (policyNumber: string) => void;
}

const ExposureChangesTable: React.FC<Props> = ({
  newExposureMap,
  modifiedRows,
  originalRows,
  onRemovePolicy,
  onOpenPathDrawer,
}) => {
  // Build table data from exposure map
  const tableData = useMemo(() => {
    return Object.entries(newExposureMap).map(([policyNumber, changes]) => {
      const policy = modifiedRows.find((row) => row.policy_number === policyNumber);
      const original = originalRows.find((row) => row.policy_number === policyNumber);

      const { stock: actualStock, currency: actualCurrency, foreign: actualForeign } =
        SimulationChangeDetectionUtil.calcActualExposures(original, policy);

      return {
        policyNumber,
        productType: policy ? formatProductType(policy.product_type) : policyNumber,
        fundsSum: policy?.funds_sum ?? 0,
        stockMarket: changes.stock_market_percent,
        foreignCurrency: changes.foreign_currency_exposure_percent,
        foreignExposure: changes.foreign_exposure_percent,
        actualStock,
        actualCurrency,
        actualForeign,
      };
    });
  }, [newExposureMap, modifiedRows]);

  if (tableData.length === 0) {
    return null;
  }

  const renderChangeCell = (change?: { old_value: number; new_value: number }, actualValue?: number | null) => {
    if (!change) {
      return <Typography variant="body2">-</Typography>;
    }

    const { old_value, new_value } = change;
    const isIncrease = new_value > old_value;
    const isDecrease = new_value < old_value;

    const hasFund = actualValue != null;
    const matched = hasFund && Math.abs(actualValue - new_value) <= 10;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, direction: "rtl" }}>
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {formatPercentage(old_value)}
          </Typography>
          <ArrowBackIcon sx={{ fontSize: 14, color: "gray" }} />
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: "bold",
              color: isIncrease ? "#2e7d32" : isDecrease ? "#d32f2f" : "inherit",
            }}
          >
            {formatPercentage(new_value)}
          </Typography>
        </Box>
        {hasFund && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 0.5 }}>
            <Box sx={{ backgroundColor: matched ? "#2e7d32" : "#c62828", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16 }}>
              {matched
                ? <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                : <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />}
            </Box>
            <Typography variant="caption" sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
              {actualValue.toFixed(0)}%
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Paper elevation={2} sx={{ mt: 2, p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        שינויי חשיפה מבוקשים
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="right">מוצר</TableCell>
            <TableCell align="right">סכום</TableCell>
            <TableCell align="right">חשיפה למניות</TableCell>
            <TableCell align="right">חשיפה למט"ח</TableCell>
            <TableCell align="right">חשיפה לחו"ל</TableCell>
            <TableCell align="center" sx={{ width: 60 }}>
              בחר מסלול
            </TableCell>
            <TableCell align="center" sx={{ width: 60 }}>
              מחק
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row) => (
            <TableRow key={row.policyNumber}>
              <TableCell align="right">
                <Typography variant="body2">{row.productType}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{formatNumberFloor(row.fundsSum)}</Typography>
              </TableCell>
              <TableCell align="right">{renderChangeCell(row.stockMarket, row.actualStock)}</TableCell>
              <TableCell align="right">{renderChangeCell(row.foreignCurrency, row.actualCurrency)}</TableCell>
              <TableCell align="right">{renderChangeCell(row.foreignExposure, row.actualForeign)}</TableCell>
              <TableCell align="center">
                {onOpenPathDrawer && (
                  <Tooltip title="בחר מסלול חדש">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onOpenPathDrawer(row.policyNumber)}
                    >
                      <CallSplitIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell align="center">
                <Tooltip title="מחק שינויים והחזר למצב המקורי">
                  <IconButton
                    size="small"
                    color="default"
                    onClick={() => onRemovePolicy(row.policyNumber)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default ExposureChangesTable;
